import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plug, Loader2, Link2, Unlink, Save, Zap, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/render";
import { toast } from "sonner";

export default function IntegrationsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/base44");
      setCfg(data);
      setBaseUrl(data.base_url || "");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setCfg({}); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { base_url: baseUrl };
      if (apiKey) payload.api_key = apiKey;
      const { data } = await api.put("/integrations/base44", payload);
      setCfg(data); setApiKey("");
      toast.success("Base44 settings saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async () => {
    setToggling(true);
    try {
      const next = !cfg?.connected;
      const { data } = await api.post("/integrations/base44/toggle", { connected: next });
      setCfg(data);
      toast.success(next ? "Base44 app connected" : "Base44 app disconnected");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setToggling(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/base44/test");
      if (data.ok) toast.success(`Reachable · ${data.detail}`);
      else toast.warning(`Unreachable · ${data.detail}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const connected = !!cfg?.connected;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Plug className="h-6 w-6 text-indigo-500" /> Integrations
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Connect external frontends and apps to the CITIDAN backend</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-border bg-card p-6 max-w-2xl" data-testid="base44-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/15 flex items-center justify-center">
              <Zap className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Base44 Frontend</h2>
              <p className="text-xs text-muted-foreground">Link an external Base44 app to this backend</p>
            </div>
          </div>
          <Badge data-testid="base44-status" className={connected
            ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30"
            : "bg-muted text-muted-foreground border-border"}>
            {connected ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Connected</> : <><XCircle className="h-3 w-3 mr-1" /> Disconnected</>}
          </Badge>
        </div>

        {cfg === null ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Base URL</Label>
              <Input data-testid="base44-url" value={baseUrl} disabled={!isAdmin}
                onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://your-app.base44.app"
                className="bg-muted/40 border-border font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">API Key</Label>
              <Input data-testid="base44-key" type="password" value={apiKey} disabled={!isAdmin}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={cfg?.has_api_key ? "•••••••• saved — type to replace" : "Paste the Base44 app API key"}
                className="bg-muted/40 border-border font-mono" />
              {cfg?.has_api_key && <p className="text-[11px] text-muted-foreground">A key is stored (encrypted). Leave blank to keep it.</p>}
            </div>

            {cfg?.last_test && (
              <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs flex items-center gap-2" data-testid="base44-last-test">
                {cfg.last_test.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                <span className="text-muted-foreground">Last test: {cfg.last_test.detail} · {fmtDate(cfg.last_test.at)}</span>
              </div>
            )}

            {isAdmin && (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <Button data-testid="base44-save" onClick={save} disabled={saving} variant="outline" className="border-border">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
                </Button>
                <Button data-testid="base44-test" onClick={test} disabled={testing || !baseUrl} variant="outline" className="border-border">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4 mr-1.5" /> Test connection</>}
                </Button>
                <Button data-testid="base44-toggle" onClick={toggle} disabled={toggling}
                  className={connected ? "bg-red-500 hover:bg-red-600 text-white" : "bg-indigo-500 hover:bg-indigo-600 text-white"}>
                  {toggling ? <Loader2 className="h-4 w-4 animate-spin" />
                    : connected ? <><Unlink className="h-4 w-4 mr-1.5" /> Disconnect</> : <><Link2 className="h-4 w-4 mr-1.5" /> Connect</>}
                </Button>
              </div>
            )}
            {!isAdmin && <p className="text-xs text-muted-foreground">Only administrators can change this connection.</p>}

            <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              To import exported Base44 code (ZIP), upload it in chat and it will be wired to this backend. This panel manages the live link (base URL + API key) and the connect/disconnect toggle.
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
